import { Timestamp } from 'firebase/firestore';
import { ThemePreset } from './theme';

export interface ThemeColors {
  logo: {
    primary: string;
    secondary: string;
    tertiary: string;
    accent: string;
    highlight: string;
  };
  primary: string;
  secondary: string;
  tertiary: string;
  quaternary: string;
  accent1: string;
  accent2: string;
  background: string;
  surface: string;
  surfaceAlt: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  error: string;
  warning: string;
  info: string;
  shadow: string;
}

export interface NotificationPreferences {
  reminders: boolean;
  general: boolean;
}

export interface PrivacyPreferences {
  analytics: boolean;
  cookies: boolean;
  dataCollection: boolean;
  isPublicProfile: boolean;
  shareActivity: boolean;
}

export interface LocationPreferences {
  latitude?: number;
  longitude?: number;
  locationName?: string;
  permissionGranted: boolean;
  lastUpdated?: number;
}

export interface PushNotificationDevice {
  deviceId: string;
  token: string;
  label: string;
  platform: string;
  browser: string;
  language?: string;
  enabled: boolean;
  grantedAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  lastSeenAt?: Timestamp | Date;
}

export type ProactivityLevel = 'quiet' | 'balanced' | 'more';
export type CuriosityFrequency = 'off' | 'gentle' | 'lively' | 'playful';

export interface ProactivityPreferences {
  level: ProactivityLevel;
  activeHourStart: number;
  activeHourEnd: number;
  nudgeAfterHours: 24 | 72 | 168;
  aiSignalsEnabled: boolean;
  heartbeatNotificationsEnabled: boolean;
  curiosityFrequency: CuriosityFrequency;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  notifications: NotificationPreferences;
  privacy: PrivacyPreferences;
  themeColors: ThemeColors;
  customThemes?: ThemePreset[];
  location: LocationPreferences;
  automation: {
    enhancedCaptureInterpretation: boolean;
    proactivity: ProactivityPreferences;
  };
}

export interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  name?: string;
  displayName?: string;
  photoURL?: string;
  photoStoragePath?: string;
  photoByteSize?: number;
  bio?: string;
  location?: string;
  website?: string;
  language?: string;
  timezone?: string;
  accountStatus: 'active' | 'suspended' | 'deleted';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastLogin?: Timestamp;
  pushNotificationToken?: string | null;
  pushNotificationEnabled?: boolean;
  pushNotificationDevices?: PushNotificationDevice[];

  preferences: UserPreferences;
}

export const defaultUserPreferences: UserPreferences = {
  theme: 'system',
  notifications: {
    reminders: true,
    general: true
  },
  themeColors: {
    primary: '#0A84FF',      // Contemporary blue
    secondary: '#8E8E93',    // Neutral gray
    tertiary: '#5E5CE6',     // Soft violet
    quaternary: '#30D158',   // Fresh green
    accent1: '#BF5AF2',      // Muted purple
    accent2: '#32D74B',      // Accent green
    background: '#ffffff',
    surface: '#F8F8F8',
    surfaceAlt: '#F2F2F2',
    text: '#111827',
    textSecondary: '#4B5563',
    border: '#E5E7EB',
    success: '#059669',
    error: '#DC2626',
    warning: '#D97706',
    info: '#2563EB',
    shadow: '#000000',
    logo: {
      primary: '#54a066',
      secondary: '#0A84FF',
      tertiary: '#616e81',
      accent: '#2f784c',
      highlight: '#c3cfb5'
    }
  },
  privacy: {
    analytics: false,
    cookies: false,
    dataCollection: false,
    isPublicProfile: false,
    shareActivity: false
  },
  location: {
    permissionGranted: false
  },
  automation: {
    enhancedCaptureInterpretation: true,
    proactivity: {
      level: 'balanced',
      activeHourStart: 9,
      activeHourEnd: 21,
      nudgeAfterHours: 72,
      aiSignalsEnabled: true,
      heartbeatNotificationsEnabled: true,
      curiosityFrequency: 'lively'
    }
  }
};
